<template>
  <v-form>
    <v-container fluid>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <v-text-field
              v-model="item.name"
              :error-messages="nameErrors"
              :label="$t('global.name')"
              required
              @input="$v.item.name.$touch()"
              @blur="$v.item.name.$touch()"
          />
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <v-text-field
              v-model="item.address"
              :error-messages="addressErrors"
              :label="$t('printer.address')"
              required
              @input="$v.item.address.$touch()"
              @blur="$v.item.address.$touch()"
          />
        </v-col>

      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <v-row align="center">
            <v-col md="8" >
              <strong class="ml-4" >{{ $t('printer.api') }}:</strong>
            </v-col>
            <v-col md="4">
              <v-switch
                  v-model="item.api"
                  :error-messages="apiErrors"
              ></v-switch>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <v-combobox
              v-if="machineSelectItems"
              v-model="item.machine"
              :items="machineSelectItems"
              :error-messages="machineErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('printer.machine')"
              item-text="name"
              item-value="@id"
          />
        </v-col>

      </v-row>
      <v-row>

        <v-col cols="12" sm="6" md="6">

          <v-row align="center">
            <v-col md="8" >
              <strong class="ml-4" >{{ $t('printer.enabled') }}:</strong>
            </v-col>
            <v-col md="4">
              <v-switch
                  v-model="item.enabled"
                  :error-messages="enabledErrors"
              ></v-switch>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <v-combobox
              v-if="tagSelectItems"
              v-model="item.tags"
              :items="tagSelectItems"
              :error-messages="tagErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('designfile.tags')"
              item-text="name"
              item-value="@id"
              :search-input.sync="search"
              multiple
              chips
              @change="changeTag()"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('global.no_result') }} "<strong>{{ search }}</strong>". {{ $t('global.addcombo1') }} <kbd>enter</kbd> {{ $t('global.addcombo2') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-slot:selection="{ attrs, item, parent, selected }">
              <v-chip
                  v-if="item === Object(item)"
                  v-bind="attrs"
                  color="accent"
                  :input-value="selected"
                  label
              >
                     <span class="pr-2">
                       {{ item.name }}
                     </span>
                <v-icon
                    small
                    @click="parent.selectItem(item)"
                >
                  mdi-close
                </v-icon>
              </v-chip>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <v-row align="center">
            <v-col md="8" >
              <strong class="ml-4" >{{ $t('printer.outOfOrder') }}:</strong>
            </v-col>
            <v-col md="4">
              <v-switch
                  v-model="item.outOfOrder"
                  :error-messages="outOfOrderErrors"
              ></v-switch>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <Loading :visible="isLoading" />
  </v-form>
</template>

<script>
import has from 'lodash/has';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import Loading from '../../components/Loading';

export default {
  name: 'PrinterForm',
  mixins: [validationMixin],
  components: {
    Loading,
  },
  props: {
    values: {
      type: Object,
      required: true
    },
    defaultvalues: {
      type: Object,
      required: false
    },
    errors: {
      type: Object,
      default: () => {}
    },

    initialValues: {
      type: Object,
      default: () => {}
    }
  },
  mounted() {
    this.machineGetSelectItems();
    this.tagGetSelectItems();
  },
  data() {
    return {
      name: null,
      address: null,
      machine: null,
      api: null,
      enabled: null,
      tags: null,
      search: null,
      outOfOrder: 0,
    };
  },
  computed: {
    isLoading() {
      return (this.isLoading_machine

      );
    },

    ...mapFields('machine', {
      machineSelectItems: 'selectItems',
      isLoading_machine : 'isLoading'
    }),
    ...mapFields('tag', {
      tagSelectItems: 'selectItems',
      isLoading_tag : 'isLoading'
    }),
    // eslint-disable-next-line
    item() {
      return this.initialValues || this.values;
    },

    nameErrors() {
      const errors = [];

      if (!this.$v.item.name.$dirty) return errors;

      has(this.violations, 'name') && errors.push(this.violations.name);

      !this.$v.item.name.required && errors.push(this.$t('global.field_required'));

      return errors;
    },
    addressErrors() {
      const errors = [];
      if (!this.$v.item.address.$dirty) return errors;

      has(this.violations, 'address') && errors.push(this.violations.address);

      !this.$v.item.address.required && errors.push(this.$t('global.field_required'));
      return errors;
    },
    machineErrors() {
      const errors = [];
      if (!this.$v.item.machine.$dirty) return errors;
      has(this.violations, 'machine') && errors.push(this.violations.machine);
      !this.$v.item.machine.required && errors.push(this.$t('global.field_required'));
      return errors;
    },
    apiErrors() {
      const errors = [];

      if (!this.$v.item.api.$dirty) return errors;

      has(this.violations, 'api') && errors.push(this.violations.api);


      return errors;
    },
    outOfOrderErrors() {
      const errors = [];

      if (!this.$v.item.outOfOrder.$dirty) return errors;

      has(this.violations, 'outOfOrder') && errors.push(this.violations.outOfOrder);


      return errors;
    },
    enabledErrors() {
      const errors = [];

      if (!this.$v.item.enabled.$dirty) return errors;

      has(this.violations, 'enabled') && errors.push(this.violations.enabled);


      return errors;
    },
    tagErrors() {
      const errors = [];

      if (!this.$v.item.tags.$dirty) return errors;

      has(this.violations, 'tags') && errors.push(this.violations.tags);


      return errors;
    },
    violations() {
      return this.errors || {};
    }
  },
  methods: {
    ...mapActions({
      machineGetSelectItems: 'machine/fetchSelectItems',
    }),
    ...mapActions({
      tagGetSelectItems: 'tag/fetchSelectItems'
    }),
    changeTag() {
      this.item.tags = this.item.tags.map(v => {
        if (typeof v === 'string') {
          v = {
            name: v,
          }
        }
        return v
      })
      this.search = ""
    }
  },
  validations: {
    item: {
      name: {
        required,
      },
      address: {
        required,
      },
      machine: {
        required,
      },
      tags: {
      },
      api: {
      },
      enabled: {
      },
      outOfOrder: {
      }
    }
  }
};
</script>
